import type { ColumnDef } from '@tanstack/react-table'
import { useEffect, useMemo, useState } from 'react'
import router, { useRouter } from 'next/router'
import { css } from '@emotion/css'
import { useMutation } from '@tanstack/react-query'
import clsx from 'clsx'
import { DataTable } from 'design-system/ui/molecules/table'
import { CopyIcon, EyeIcon, EyeOffIcon } from 'lucide-react'

import type { GetTriggerInstancesActiveResponse } from '~/generated/client'
import { changeTriggerStatus } from '~/dataProcessor/api/api'
import { UpdateQuery } from '~/dataProcessor/hooks/global/router'
import { Button } from '~/design-system/atom/button'
import { Switch } from '~/design-system/atom/switch'
import { useToast } from '~/design-system/atom/use-toast'
import { useGetTriggerInstanceData } from '~/hooks/query/triggers'
import { ErrorBoundary } from '~/ui/components/base/errorBoundary'
import { IdDropDown } from '~/ui/components/base/idDropDown'
import { PaginationComponent } from '~/ui/components/custom/PaginationComponent'
import { formatActionName, showFirstAndLastNLetters } from '~/utils/string'
import { RightSideContent } from '../../containers/base/base'
import { ActionButton } from '../../containers/common/ActionButton'

export const PaginationFilter = ({
  totalPage = '',
}: {
  totalPage: string | number
}) => {
  const { query } = useRouter()

  const updateQuery = UpdateQuery()
  const showDisabled = query.showDisabled === 'true'

  return (
    <div className="mt-4 flex items-center justify-between px-4 text-[14px] text-black-200">
      <div className="flex items-center gap-2">
        <ToggleButton
          title={showDisabled ? 'Hide inactive' : 'Show inactive'}
          onClick={() => {
            updateQuery('showDisabled', !showDisabled ? 'true' : 'false')
          }}
          isActive={showDisabled}
        />
      </div>
      <div className="flex items-center gap-2">
        <PaginationComponent
          currentPage={Number(query.page) || 1}
          totalPages={Number(totalPage) || 1}
          onChange={(page) => {
            updateQuery('page', page.toString())
          }}
        />
      </div>
    </div>
  )
}

export const ShowInactiveButtonWQuery = () => {
  const { query } = useRouter()
  const showDisabled = query.showDisabled === 'true'

  const updateQuery = UpdateQuery()

  return (
    <ToggleButton
      title={showDisabled ? 'Hide inactive' : 'Show inactive'}
      onClick={() => {
        updateQuery('showDisabled', !showDisabled ? 'true' : 'false')
      }}
      isActive={showDisabled}
    />
  )
}

const ToggleButton = ({
  title,
  onClick,
  isActive,
}: {
  title: string
  onClick: () => void
  isActive: boolean
}) => {
  return (
    <div
      className="flex flex-1 cursor-pointer items-center gap-2 overflow-hidden rounded-[20px] border border-grey-200 bg-[#fff] px-4 py-2 text-black-600 hover:border-grey-600 hover:bg-[#ffffffc9]"
      onClick={onClick}
    >
      <div className="mt-[2px] text-[13px] font-[400] leading-[100%]">
        {title}
      </div>
      {!isActive ? (
        <EyeOffIcon className="h-[14px] w-[14px]" color="#000" />
      ) : (
        <EyeIcon className="h-[14px] w-[14px]" />
      )}
    </div>
  )
}

export const TriggersScreen = () => {
  const { query } = useRouter()

  const { triggerData } = useGetTriggerInstanceData()

  const triggerDataById = useMemo(() => {
    return triggerData?.items?.find(
      (trigger) => trigger.trigger_name === query.name,
    )
  }, [triggerData])

  return (
    <RightSideContent
      header={
        <div className="w-full">
          <div>
            <span className="min-w-[120px] text-black-200">
              {formatActionName(
                triggerDataById?.trigger_name || 'Active triggers',
              )}
            </span>
          </div>
        </div>
      }
    >
      <div className={clsx(css``)}>
        <div className={'w-full pb-10'}>
          <ErrorBoundary
            fallback={
              <div>{`Something went wrong. We'll work on getting this fixed.`}</div>
            }
          >
            <TriggerInstanceRenderer />
          </ErrorBoundary>
        </div>
      </div>
    </RightSideContent>
  )
}

// Define a type for the cell info parameter
interface CellProps {
  row: {
    original: GetTriggerInstancesActiveResponse['items'][number]
  }
}

// Cell renderer components
const TriggerIdCell = ({ row }: CellProps) => {
  const triggerInstance = row.original

  const { toast } = useToast()
  return (
    <div
      className="flex w-fit items-center gap-1 text-[13px] font-[400] text-black-600"
      onClick={(e) => {
        e.preventDefault()
        e.stopPropagation()
      }}
    >
      <IdDropDown
        ids={[
          {
            key: 'v1/v2',
            label: 'v1/v2 (uuid)',
            id: triggerInstance.uuid!,
          },
          {
            key: 'v3',
            label: 'v3 (nanoid)',
            id: triggerInstance.id,
          },
        ]}
        onCopy={() => {
          toast({
            title: 'Copied',
            description: 'Trigger ID copied to clipboard',
          })
        }}
      >
        <div className="flex items-center gap-1">
          <span>{showFirstAndLastNLetters(triggerInstance.id)}</span>
          <CopyIcon className="cursor-pointer" height={12} width={12} />
        </div>
      </IdDropDown>
    </div>
  )
}

const EntityIdCell = ({ row }: CellProps) => {
  const clientUniqueUserId = row.original.connected_account_id
  return (
    <div className="h-[18px] overflow-hidden text-ellipsis whitespace-nowrap text-[13px]">
      {clientUniqueUserId || '--'}
    </div>
  )
}

const LastUpdatedCell = ({ row }: CellProps) => {
  return (
    <div className="h-[18px] overflow-hidden text-ellipsis whitespace-nowrap text-[13px]">
      {new Date(row.original.updated_at).toLocaleString()}
    </div>
  )
}

const TriggerNameCell = ({ row }: CellProps) => {
  return (
    <div className="h-[18px] overflow-hidden text-ellipsis whitespace-nowrap text-[13px]">
      {row.original.trigger_name}
    </div>
  )
}

const ConfigCell = ({ row }: CellProps) => {
  return (
    <div
      className="h-[18px] overflow-hidden text-ellipsis whitespace-nowrap text-[13px]"
      title={JSON.stringify(row.original.trigger_config)}
      onClick={(e) => {
        e.preventDefault()
        e.stopPropagation()
      }}
    >
      {JSON.stringify(row.original.trigger_config)}
    </div>
  )
}

const ActionsCell = ({ row }: CellProps) => {
  const triggerInstance = row.original
  const { toast } = useToast()
  const [isEnabled, setIsEnabled] = useState(
    triggerInstance.disabled_at === null,
  )

  const { mutate } = useMutation(changeTriggerStatus, {
    onError: () => {
      toast({
        title: 'Error',
        description: 'Failed to update trigger status',
        variant: 'destructive',
      })

      // Revert the switch to its original state in case of error
      setIsEnabled(!isEnabled)
    },
  })

  const handleStatusChange = (enabled: boolean) => {
    setIsEnabled(enabled)
    // Call the mutation function
    mutate({
      triggerId: triggerInstance.id,
      enabled,
    })
  }

  return (
    <div
      className="flex items-center gap-1"
      onClick={(e) => {
        e.preventDefault()
        e.stopPropagation()
      }}
    >
      <Switch
        id={`switch-${triggerInstance.id}`}
        checked={isEnabled}
        onCheckedChange={(e) => {
          handleStatusChange(e)
        }}
      />
      <Button
        variant={'plain'}
        size={28}
        className="ml-2 bg-[white]"
        onClick={() =>
          void router.push(`/trigger_logs?search=${triggerInstance.id}`)
        }
      >
        View Logs
      </Button>
      <ActionButton triggerId={triggerInstance.id} />
    </div>
  )
}

export const TriggerInstanceRenderer = () => {
  const router = useRouter()
  const { query } = router
  const {
    triggerData: data,
    isLoading,
    isError,
    isSuccess,
  } = useGetTriggerInstanceData()

  console.log(data)

  const updateQuery = UpdateQuery()

  useEffect(() => {
    if (!router.isReady) return

    if (!query.page) updateQuery('page', '1')
  }, [router.query])

  // Define columns for DataTable
  const columns = useMemo<
    ColumnDef<GetTriggerInstancesActiveResponse['items'][number], unknown>[]
  >(
    () => [
      {
        accessorKey: 'id',
        header: 'Trigger ID',
        size: 140,
        cell: ({ row }) => <TriggerIdCell row={row} />,
      },
      {
        accessorKey: 'connection.clientUniqueUserId',
        header: 'User ID',
        size: 120,
        cell: ({ row }) => <EntityIdCell row={row} />,
      },
      {
        accessorKey: 'updatedAt',
        header: 'Last Updated',
        size: 180,
        cell: ({ row }) => <LastUpdatedCell row={row} />,
      },
      {
        accessorKey: 'triggerName',
        header: 'Trigger Name',
        size: 200,
        cell: ({ row }) => <TriggerNameCell row={row} />,
      },
      {
        accessorKey: 'triggerConfig',
        header: 'Config',
        size: 300,
        cell: ({ row }) => <ConfigCell row={row} />,
      },
      {
        id: 'actions',
        header: () => <div className="pr-4 text-right">Actions</div>,
        size: 300,
        cell: ({ row }) => <ActionsCell row={row} />,
      },
    ],
    [],
  )

  // Empty state component
  const emptyState = (
    <div className="flex flex-col items-center justify-center gap-[12px]">
      <div className="text-[17px] font-[500] text-black-200">
        No active triggers enabled
      </div>
      <div className="text-[14.5px] font-[400] text-black-600">
        Triggers let you perform actions on events from connected providers.
      </div>
      <div className="text-[14.5px] font-[400] text-black-600">
        Enable triggers on integrations or connected accounts to see them here.
      </div>
    </div>
  )
  // Error component
  const errorComponent = (
    <div className="mt-10 h-full w-full text-center text-red-500">
      Failed to fetch trigger data. Please try again.
    </div>
  )

  return (
    <div className="h-full w-full">
      <PaginationFilter totalPage={data?.total_pages || ''} />
      <div className="mt-[18px]">
        <DataTable
          columns={columns}
          data={data?.items || []}
          isLoading={isLoading}
          isError={isError}
          emptyState={emptyState}
          errorComponent={errorComponent}
          onRowClick={(
            row: GetTriggerInstancesActiveResponse['items'][number],
          ) => {
            router.push(`/logs?search=${row.id}`)
          }}
        />
      </div>
    </div>
  )
}

export const gridBox = css`
  display: grid;
  grid-template-columns: 100px 80px 140px 140px 320px 1fr;
  gap: 16px;
  width: 100%;
`
